// src/features/works/components/AssignPersonsToWork.jsx
import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button, ListGroup, InputGroup, Spinner } from 'react-bootstrap';
import { FaSearch, FaArrowRight, FaArrowLeft, FaSave, FaTimes } from 'react-icons/fa';

// Servicios
import PeopleService from '../../people/PeopleService';
import WorksService from '../WorksService';

/**
 * Componente para asignar/desasignar personas a una obra
 * Inspirado en el componente antiguo MaterialUI pero implementado con React Bootstrap
 * 
 * @param {string} workId - ID de la obra
 * @param {Array} currentPersons - Lista de personas actualmente asignadas a la obra
 * @param {Function} onSave - Callback cuando se guardan los cambios
 * @param {Function} onCancel - Callback cuando se cancela la operación
 */
function AssignPersonsToWork({ workId, currentPersons = [], onSave, onCancel }) {
  // Estados
  const [loading, setLoading] = useState(true);
  const [savingChanges, setSavingChanges] = useState(false);
  const [allPersons, setAllPersons] = useState([]);
  const [assignedPersons, setAssignedPersons] = useState([]);
  const [unassignedPersons, setUnassignedPersons] = useState([]);
  const [selectedAssigned, setSelectedAssigned] = useState([]);
  const [selectedUnassigned, setSelectedUnassigned] = useState([]);
  const [filterAssigned, setFilterAssigned] = useState('');
  const [filterUnassigned, setFilterUnassigned] = useState('');

  // Cargar personas al iniciar
  useEffect(() => {
    loadPersons();
  }, []);

  // Cargar personas y clasificarlas
  const loadPersons = async () => {
    try {
      setLoading(true);
      
      // Obtener todas las personas (no clientes)
      const personsData = await PeopleService.getAllPeople();
      
      // Filtrar solo personas físicas (no empresas) que no sean clientes
      const filteredPersons = personsData.filter(person => 
        person.type === 'F' && person.regime !== 'Cliente'
      );
      
      setAllPersons(filteredPersons);
      
      // Crear listas de asignados/no asignados
      classifyPersons(filteredPersons, currentPersons);
    } catch (error) {
      console.error('Error al cargar personas:', error);
    } finally {
      setLoading(false);
    }
  };

  // Clasificar personas en asignadas y no asignadas
  const classifyPersons = (allPersonsList, currentPersonsList) => {
    const currentIds = currentPersonsList.map(p => p.id);
    
    const assigned = allPersonsList.filter(person => 
      currentIds.includes(person.id)
    );
    
    const unassigned = allPersonsList.filter(person => 
      !currentIds.includes(person.id)
    );
    
    setAssignedPersons(assigned);
    setUnassignedPersons(unassigned);
  };

  // Filtrar lista de personas asignadas
  const filteredAssignedPersons = assignedPersons.filter(person => {
    if (!filterAssigned) return true;
    
    const searchTerm = filterAssigned.toLowerCase();
    const fullName = `${person.firstName} ${person.surname || ''}`.toLowerCase();
    const documentValue = (person.document || '').toLowerCase();
    
    return fullName.includes(searchTerm) || documentValue.includes(searchTerm);
  });

  // Filtrar lista de personas no asignadas
  const filteredUnassignedPersons = unassignedPersons.filter(person => {
    if (!filterUnassigned) return true;
    
    const searchTerm = filterUnassigned.toLowerCase();
    const fullName = `${person.firstName} ${person.surname || ''}`.toLowerCase();
    const documentValue = (person.document || '').toLowerCase();
    
    return fullName.includes(searchTerm) || documentValue.includes(searchTerm);
  });

  // Asignar personas seleccionadas
  const handleAssignPersons = () => {
    // Encontrar las personas seleccionadas
    const personsToAssign = unassignedPersons.filter(person => 
      selectedUnassigned.includes(person.id)
    );
    
    // Mover a lista de asignados
    setAssignedPersons([...assignedPersons, ...personsToAssign]);
    
    // Eliminar de lista de no asignados
    setUnassignedPersons(unassignedPersons.filter(person => 
      !selectedUnassigned.includes(person.id)
    ));
    
    // Limpiar selección
    setSelectedUnassigned([]);
  };

  // Desasignar personas seleccionadas
  const handleUnassignPersons = () => {
    // Encontrar las personas seleccionadas
    const personsToUnassign = assignedPersons.filter(person => 
      selectedAssigned.includes(person.id)
    );
    
    // Mover a lista de no asignados
    setUnassignedPersons([...unassignedPersons, ...personsToUnassign]);
    
    // Eliminar de lista de asignados
    setAssignedPersons(assignedPersons.filter(person => 
      !selectedAssigned.includes(person.id)
    ));
    
    // Limpiar selección
    setSelectedAssigned([]);
  };

  // Manejar selección en lista de asignados
  const handleSelectAssigned = (personId) => {
    // Verificar si ya está seleccionada
    if (selectedAssigned.includes(personId)) {
      // Quitar de selección
      setSelectedAssigned(selectedAssigned.filter(id => id !== personId));
    } else {
      // Agregar a selección
      setSelectedAssigned([...selectedAssigned, personId]);
    }
  };

  // Manejar selección en lista de no asignados
  const handleSelectUnassigned = (personId) => {
    // Verificar si ya está seleccionada
    if (selectedUnassigned.includes(personId)) {
      // Quitar de selección
      setSelectedUnassigned(selectedUnassigned.filter(id => id !== personId));
    } else {
      // Agregar a selección
      setSelectedUnassigned([...selectedUnassigned, personId]);
    }
  };

  // Guardar cambios en la API
  const handleSaveChanges = async () => {
    try {
      setSavingChanges(true);
      
      // Obtener IDs de personas asignadas
      const personIds = assignedPersons.map(person => person.id);
      
      // Llamar al servicio para actualizar asignaciones
      await WorksService.assignPersonsToWork(personIds, workId);
      
      // Llamar al callback de éxito
      if (onSave) onSave();
    } catch (error) {
      console.error('Error al guardar asignaciones:', error);
      alert('Ocurrió un error al guardar los cambios. Por favor, inténtelo nuevamente.');
    } finally {
      setSavingChanges(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
        <p className="mt-2">Cargando datos de personas...</p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-muted mb-3">
        Seleccione las personas que desea asignar o desasignar de esta obra.
      </p>
      
      <Row>
        {/* Lista de personas no asignadas */}
        <Col md={5}>
          <h6 className="mb-2">Personas Disponibles</h6>
          
          <InputGroup className="mb-2">
            <Form.Control
              placeholder="Buscar por nombre o documento..."
              value={filterUnassigned}
              onChange={(e) => setFilterUnassigned(e.target.value)}
            />
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
          </InputGroup>
          
          <div style={{ height: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.25rem' }}>
            <ListGroup variant="flush">
              {filteredUnassignedPersons.length === 0 ? (
                <ListGroup.Item className="text-center text-muted py-4">
                  No hay personas disponibles
                </ListGroup.Item>
              ) : (
                filteredUnassignedPersons.map(person => (
                  <ListGroup.Item 
                    key={person.id}
                    action
                    active={selectedUnassigned.includes(person.id)}
                    onClick={() => handleSelectUnassigned(person.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex justify-content-between">
                      <div>
                        {person.firstName} {person.surname || ''}
                      </div>
                      <div className="text-muted">
                        {person.document || ''}
                      </div>
                    </div>
                  </ListGroup.Item>
                ))
              )}
            </ListGroup>
          </div>
        </Col>
        
        {/* Botones de acción */}
        <Col md={2} className="d-flex flex-column align-items-center justify-content-center">
          <Button 
            variant="primary"
            size="sm"
            className="mb-2 w-75"
            onClick={handleAssignPersons}
            disabled={selectedUnassigned.length === 0}
          >
            <FaArrowRight className="me-1" /> Asignar
          </Button>
          
          <Button 
            variant="primary"
            size="sm"
            className="mb-2 w-75"
            onClick={handleUnassignPersons}
            disabled={selectedAssigned.length === 0}
          >
            <FaArrowLeft className="me-1" /> Quitar
          </Button>
        </Col>
        
        {/* Lista de personas asignadas */}
        <Col md={5}>
          <h6 className="mb-2">Personas Asignadas</h6>
          
          <InputGroup className="mb-2">
            <Form.Control
              placeholder="Buscar por nombre o documento..."
              value={filterAssigned}
              onChange={(e) => setFilterAssigned(e.target.value)}
            />
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
          </InputGroup>
          
          <div style={{ height: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.25rem' }}>
            <ListGroup variant="flush">
              {filteredAssignedPersons.length === 0 ? (
                <ListGroup.Item className="text-center text-muted py-4">
                  No hay personas asignadas
                </ListGroup.Item>
              ) : (
                filteredAssignedPersons.map(person => (
                  <ListGroup.Item 
                    key={person.id}
                    action
                    active={selectedAssigned.includes(person.id)}
                    onClick={() => handleSelectAssigned(person.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex justify-content-between">
                      <div>
                        {person.firstName} {person.surname || ''}
                      </div>
                      <div className="text-muted">
                        {person.document || ''}
                      </div>
                    </div>
                  </ListGroup.Item>
                ))
              )}
            </ListGroup>
          </div>
        </Col>
      </Row>
      
      {/* Botones de guardar y cancelar */}
      <div className="d-flex justify-content-end mt-4">
        <Button 
          variant="secondary"
          onClick={onCancel}
          className="me-2"
          disabled={savingChanges}
        >
          <FaTimes className="me-1" /> Cancelar
        </Button>
        <Button 
          variant="primary"
          onClick={handleSaveChanges}
          disabled={savingChanges}
        >
          {savingChanges ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-1"
              />
              Guardando...
            </>
          ) : (
            <>
              <FaSave className="me-1" /> Guardar Cambios
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

export default AssignPersonsToWork;